import { useState } from 'react';
import { getBillingStripePromise, IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';

/**
 * The Stripe.js instance for one `<Elements>` boundary, resolved once for the lifetime of that boundary.
 *
 * Read once per mount rather than once per render. Both matter only once a load has failed — an ad
 * blocker holding back the script is the usual cause — because `getBillingStripePromise` deliberately
 * stops memoising at that point so the next attempt can retry:
 *
 * - Re-reading it every render would then start a fresh script load per render, and in checkout the
 *   address fields re-render on every tax-triggering keystroke.
 * - `<Elements>` ignores a changed `stripe` prop and warns about it, so a new promise identity per render
 *   is never picked up anyway.
 *
 * One attempt per mount is also the behaviour these forms want: they are modals, so a reopened form is a
 * new mount, and that is exactly when a failed load should be retried.
 *
 * `useState` rather than `useMemo` because this has to be created exactly once, not "probably once".
 */
export function useBillingStripePromise(): ReturnType<typeof getBillingStripePromise> | null {
  const [stripe] = useState(() => (IS_STRIPE_TEST_MODE ? null : getBillingStripePromise()));
  return stripe;
}
